import { useEffect, useState, useRef } from 'react';
import { useSignalR } from 'contexts/SignalRContext';
import { useUserInfo } from 'hooks/useUserInfo';
import { trackCustomEvent } from 'utils/telemetry';

export interface VersionUpdateData {
  version: string;
  message: string;
  isMandatory: boolean;
  countdownSeconds: number;
  targetUsername?: string;
  sentBy: string;
  sentAt: string;
}

export interface SignalRVersionNotificationState {
  hasUpdate: boolean;
  versionData: VersionUpdateData | null;
  clearUpdate: () => void;
}

/**
 * Hook to listen for version update notifications via SignalR
 * 
 * This provides instant version updates pushed from the server:
 * - All clients: Broadcast updates
 * - Specific user: Targeted updates
 * - Mandatory updates: Auto-reload with countdown
 * 
 * @example
 * ```tsx
 * const { hasUpdate, versionData } = useSignalRVersionNotification();
 * 
 * if (hasUpdate && versionData) {
 *   if (versionData.isMandatory) {
 *     // Show countdown timer
 *   } else {
 *     // Show optional notification
 *   }
 * }
 * ```
 */
export function useSignalRVersionNotification(): SignalRVersionNotificationState {
  const { connection, status } = useSignalR();
  const { userName } = useUserInfo();
  const [hasUpdate, setHasUpdate] = useState(false);
  const [versionData, setVersionData] = useState<VersionUpdateData | null>(null);
  const handlerRegistered = useRef(false);

  useEffect(() => {
    // Only register handler once when connection is available
    if (connection && !handlerRegistered.current) {
      console.log('[SignalR-Version] Registering VersionUpdateAvailable handler');
      
      connection.on('VersionUpdateAvailable', (data: VersionUpdateData) => {
        console.log('[SignalR-Version] Version update received:', data);

        // Check if this update is targeted at a specific user
        if (data.targetUsername) {
          // Only process if it's for current user
          if (data.targetUsername === userName) {
            console.log('[SignalR-Version] Targeted update for current user');
            
            // Track received update in Application Insights
            trackCustomEvent('VersionControl_UpdateReceived', {
              user: userName || 'Unknown',
              targetUser: data.targetUsername || 'Unknown',
              version: data.version || 'Unknown',
              isMandatory: data.isMandatory?.toString() || 'false',
              countdownSeconds: data.countdownSeconds?.toString() || '30',
              sentBy: data.sentBy || 'Unknown',
              source: 'signalr_targeted',
              timestamp: new Date().toISOString()
            });
            
            setVersionData(data);
            setHasUpdate(true);
          } else {
            console.log('[SignalR-Version] Ignoring update targeted at:', data.targetUsername);
          }
        } else {
          // Broadcast to all users
          console.log('[SignalR-Version] Broadcast update for all users');
          
          // Track received update in Application Insights
          trackCustomEvent('VersionControl_UpdateReceived', {
            user: userName || 'Unknown',
            version: data.version || 'Unknown',
            isMandatory: data.isMandatory?.toString() || 'false',
            countdownSeconds: data.countdownSeconds?.toString() || '30',
            sentBy: data.sentBy || 'Unknown',
            source: 'signalr_broadcast',
            timestamp: new Date().toISOString()
          });
          
          setVersionData(data);
          setHasUpdate(true);
        }
      });

      handlerRegistered.current = true;

      // Cleanup handler on unmount
      return () => {
        if (connection) {
          console.log('[SignalR-Version] Unregistering VersionUpdateAvailable handler');
          connection.off('VersionUpdateAvailable');
          handlerRegistered.current = false;
        }
      };
    }
  }, [connection, userName]);

  // Log connection status changes
  useEffect(() => {
    console.log('[SignalR-Version] Connection status:', status);
  }, [status]);

  const clearUpdate = () => {
    setHasUpdate(false);
    setVersionData(null);
  };

  return {
    hasUpdate,
    versionData,
    clearUpdate
  };
}

